import React, { useState, useEffect, ChangeEvent } from "react";
import { IContact } from "../../Contact.props";
import { SubmitHandler, useForm } from "react-hook-form";
import ApiService from "../../../../../service/ApiService";
import { apiPaths } from "../../../../../service/apiPaths";
import { useNavigate, useParams } from "react-router-dom";

const useEdit = () => {
  const [loading, setLoading] = useState(false);
  const [contactData, setContactData] = useState<IContact | null>(null);
  const [interest, setInterest] = useState<any[]>([]);
  const [interestId, setInterestId] = useState<string>("");
  const [services, setServices] = useState<any[]>([]);
  const [serviceId, setServiceId] = useState<string>("");
  const [outputStatuses, setOutputStatuses] = useState<any[]>([]);
  const [outputStatusId, setOutputStatusId] = useState<string>("");
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [platformId, setPlatformId] = useState<string>("");

  const navigate = useNavigate();
  const { id } = useParams();

  const { handleSubmit, register, setValue, reset } = useForm<IContact>();

  const handleCancel = () => {
    navigate("/admin/contact");
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactData({ ...contactData, [name]: value } as IContact);
  };

  // Fetch contact data by ID
  const fetchContactData = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      // Try the first endpoint
      let result;
      try {
        result = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.singleContact}${id}`,
        });
        console.log("API Response from singleContact:", result);
      } catch (error) {
        console.log("First endpoint failed, trying alternative...");
        // Try alternative endpoint
        result = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getContact}/${id}`,
        });
        console.log("API Response from getContact:", result);
      }
      
      console.log("API Endpoint:", `${apiPaths.singleContact}${id}`);
      
      console.log("Fetched contact data:", result);
      console.log("Contact data structure:", {
        name: result?.contact.name,
        email: result?.email,
        phone: result?.phone,
        companyName: result?.companyName,
        interest: result?.interest,
        service: result?.service,
        outputStatus: result?.outputStatus,
        platform: result?.platform,
      });
      setContactData(result.contact);
      
      // Populate form with existing data
      if (result) {
        reset(result);
        
        // Set dropdown values - handle both string and object formats
        if (result.interest) {
          if (typeof result.interest === 'string') {
            setInterestId(result.interest);
          } else if (result.interest._id) {
            setInterestId(result.interest._id);
          }
        }
        
        if (result.service) {
          if (typeof result.service === 'string') {
            setServiceId(result.service);
          } else if (result.service._id) {
            setServiceId(result.service._id);
          }
        }
        
        if (result.outputStatus) {
          if (typeof result.outputStatus === 'string') {
            setOutputStatusId(result.outputStatus);
          } else if (result.outputStatus._id) {
            setOutputStatusId(result.outputStatus._id);
          }
        }
        
        if (result.platform) {
          if (typeof result.platform === 'string') {
            setPlatformId(result.platform);
          } else if (result.platform._id) {
            setPlatformId(result.platform._id);
          }
        }
      }
      
    } catch (error) {
      console.error("Error fetching contact:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactData();
  }, [id]);

  // Set form values when contactData is loaded
  useEffect(() => {
    if (contactData) {
      reset(contactData);
    }
  }, [contactData, reset]);

  const fetchInterestsData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.interests}`,
      });
      setInterest(result);
      if (result.length > 0 && !interestId) {
        setInterestId(result[0]._id || result[0].id);
      }
    } catch (error) {
      console.error("Error fetching interests:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServicesData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.services}`,
      });
      setServices(result);
      if (result.length > 0 && !serviceId) {
        setServiceId(result[0]._id || result[0].id);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputStatusesData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.outputStatus}`,
      });
      setOutputStatuses(result);
      if (result.length > 0 && !outputStatusId) {
        setOutputStatusId(result[0]._id || result[0].id);
      }
    } catch (error) {
      console.error("Error fetching output statuses:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformsData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.platforms}`,
      });
      setPlatforms(result);
      if (result.length > 0 && !platformId) {
        setPlatformId(result[0]._id || result[0].id);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit: SubmitHandler<IContact> = async (data) => {
    if (!contactData?._id) {
      console.error("No contact ID found");
      return;
    }

    setLoading(true);
    try {
      // Prepare the data with dropdown values
      const submitData = {
        ...data,
        interest: interestId,
        service: serviceId,
        outputStatus: outputStatusId,
        platform: platformId,
      };

      const result = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.editContact}${contactData._id}`,
        data: submitData,
      });
      
      if (result) {
        setContactData(result);
        navigate("/contact");
      }
    } catch (error) {
      console.error("Error submitting contact:", error);
      // You can add toast notification here for user feedback
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    contactData,
    interest,
    interestId,
    services,
    serviceId,
    outputStatuses,
    outputStatusId,
    platforms,
    platformId,
    setInterestId,
    setServiceId,
    setOutputStatusId,
    setPlatformId,
    fetchInterestsData,
    fetchServicesData,
    fetchOutputStatusesData,
    fetchPlatformsData,
    handleSubmit: handleSubmit(onSubmit),
    register,
    reset,
    handleChange,
  };
};

export default useEdit;
